import { useMemo, useState } from "react";
import { FilePlus2, Search, Sparkles } from "lucide-react";
import type { CatalogSearchFilter, ConditionSearchHit, MatchTier, SmartConditionHit } from "../../types";
import { CATALOG_SEARCH_FILTERS } from "../../types";
import { getApi } from "../../api";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { useDebounce } from "../../hooks/useDebounce";
import { Button } from "../Button";
import { Card } from "../Card";
import { EmptyState, ErrorState, Skeleton } from "../ScreenStates";
import { inputClass } from "../fields/fieldStyles";
import { ConditionResultRow } from "./ConditionResultRow";

interface ConditionSearchProps {
  /** Clicking a condition is the pick; the caller decides where it leads (a member's ratings, an actuarial editor). */
  onPick: (hit: ConditionSearchHit) => void;
  /** Actuarial screens: offer to create the typed name when no condition has it. */
  onCreate?: (name: string) => void;
  /** Actuarial screens: the All conditions list includes inactive ones so they can be reactivated. */
  browseInactive?: boolean;
  children?: never;
}

type BrowseMode = "mostUsed" | "all";

/** Alphabetical categories, each holding its conditions already sorted by name. */
function groupByCategory(hits: readonly ConditionSearchHit[]): { category: string; hits: ConditionSearchHit[] }[] {
  const groups = new Map<string, ConditionSearchHit[]>();
  for (const hit of hits) groups.set(hit.category, [...(groups.get(hit.category) ?? []), hit]);
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([category, grouped]) => ({ category, hits: grouped }));
}

const TIER_LABELS: Record<MatchTier, string> = { Exact: "Exact", Alias: "Alias", Name: "Name", Medication: "Medication", Description: "Description" };

/** The smart tier needs a few more characters than the name tiers before an embedding is worth asking for. */
const SMART_MIN_LENGTH = 3;

/** A suggestion rendered through the same row as a name hit; the matched rating is the evidence shown beside it. */
function toSuggestedHit(hit: SmartConditionHit): ConditionSearchHit {
  return { conditionId: hit.conditionId, name: hit.name, category: hit.isCustom ? "Custom" : "MUGS", isCustom: hit.isCustom, isActive: true, selectionCount: 0, matchTier: "Name", matchedText: hit.matchedRatingText || null };
}

function ResultsSkeleton() {
  return (
    <div className="space-y-2" role="status" aria-label="Searching conditions">
      {Array.from({ length: 5 }, (_, index) => (
        <Skeleton key={index} className="h-12 w-full" />
      ))}
    </div>
  );
}

/** Type-ahead search (300ms debounce) with filter chips, tiered results, and the Most Used list when empty. */
export function ConditionSearch({ onPick, onCreate, browseInactive = false }: ConditionSearchProps) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<CatalogSearchFilter>("All");
  const [browse, setBrowse] = useState<BrowseMode>("mostUsed");
  const allConditions = useAsyncResource(async () => (browse === "all" ? getApi().listConditions(browseInactive) : null), `all-${browse}-${browseInactive}`, "ConditionSearch.all");
  const categories = useMemo(() => groupByCategory(allConditions.data ?? []), [allConditions.data]);
  const debounced = useDebounce(query.trim(), 300);
  const mostUsed = useAsyncResource(() => getApi().getMostUsedConditions(15), "most-used", "ConditionSearch.mostUsed");
  const results = useAsyncResource(async () => (debounced === "" ? null : getApi().searchCatalog(debounced, filter)), `${debounced}|${filter}`, "ConditionSearch.search");
  // The smart tier (Skynet's embedding and index) runs on the same pause but costs a round trip to two services, so it renders
  // underneath the name tiers as it arrives and never holds them up.
  const smart = useAsyncResource(async () => (debounced.length < SMART_MIN_LENGTH ? null : getApi().smartSearchCatalog(debounced, filter)), `smart|${debounced}|${filter}`, "ConditionSearch.smart");
  const isSearching = debounced !== "";
  const hasExactName = results.data?.tiers.some((tier) => tier.hits.some((hit) => hit.name.toLowerCase() === debounced.toLowerCase())) ?? false;
  const offerCreate = onCreate !== undefined && isSearching && results.state.kind === "ready" && !hasExactName;
  const namedIds = useMemo(() => new Set(results.data?.tiers.flatMap((tier) => tier.hits.map((hit) => hit.conditionId)) ?? []), [results.data]);
  const suggestions = useMemo(() => (smart.data?.hits ?? []).filter((hit) => !namedIds.has(hit.conditionId)).map(toSuggestedHit), [smart.data, namedIds]);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="condition-search" className="sr-only">
          Search conditions and medications
        </label>
        <div className="relative">
          <input id="condition-search" type="search" value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search conditions or medications; shorthand like high BP or HTN works too" autoComplete="off" className={`${inputClass()} pl-10`} />
          <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        </div>
        <div role="radiogroup" aria-label="Filter results" className="mt-2 flex gap-2">
          {CATALOG_SEARCH_FILTERS.map((option) => (
            <button key={option} type="button" role="radio" aria-checked={filter === option} onClick={() => setFilter(option)} className={`min-h-[36px] cursor-pointer rounded-full border px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-green-50 ${filter === option ? "border-brand-green bg-brand-green-10 text-brand-green-dark" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}>
              {option}
            </button>
          ))}
        </div>
      </div>

      {!isSearching && (
        <Card
          title={browse === "mostUsed" ? "Most Used" : `All conditions${allConditions.data ? ` (${allConditions.data.length})` : ""}`}
          actions={
            <span className="flex items-center gap-3">
              {browse === "mostUsed" && <span className="text-xs text-gray-500">Top 15 by selection count</span>}
              <span role="radiogroup" aria-label="Browse" className="inline-flex gap-1">
                {(
                  [
                    { value: "mostUsed", label: "Most used" },
                    { value: "all", label: "View all" },
                  ] as const
                ).map((option) => (
                  <button key={option.value} type="button" role="radio" aria-checked={browse === option.value} onClick={() => setBrowse(option.value)} className={`min-h-[32px] cursor-pointer rounded-full border px-3 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-brand-green-50 ${browse === option.value ? "border-brand-green bg-brand-green-10 text-brand-green-dark" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}>
                    {option.label}
                  </button>
                ))}
              </span>
            </span>
          }
        >
          {browse === "mostUsed" && (
            <>
              {mostUsed.state.kind === "error" && <ErrorState message={mostUsed.state.message} onRetry={mostUsed.reload} />}
              {mostUsed.state.kind === "loading" && !mostUsed.data && <ResultsSkeleton />}
              {mostUsed.data && (
                <ul className="divide-y divide-gray-100">
                  {mostUsed.data.map((hit) => (
                    <ConditionResultRow key={hit.conditionId} hit={hit} onPick={onPick} />
                  ))}
                </ul>
              )}
            </>
          )}
          {browse === "all" && (
            <>
              {allConditions.state.kind === "error" && <ErrorState message={allConditions.state.message} onRetry={allConditions.reload} />}
              {allConditions.state.kind === "loading" && !allConditions.data && <ResultsSkeleton />}
              {allConditions.data &&
                categories.map((group) => (
                  <section key={group.category} aria-label={`${group.category} conditions`} className="mb-3">
                    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {group.category} <span className="font-normal text-gray-400">({group.hits.length})</span>
                    </h3>
                    <ul className="divide-y divide-gray-100">
                      {group.hits.map((hit) => (
                        <ConditionResultRow key={hit.conditionId} hit={hit} onPick={onPick} />
                      ))}
                    </ul>
                  </section>
                ))}
            </>
          )}
        </Card>
      )}

      {isSearching && (
        <Card title={`Results for "${debounced}"`}>
          {results.state.kind === "error" && <ErrorState message={results.state.message} onRetry={results.reload} />}
          {results.state.kind === "loading" && <ResultsSkeleton />}
          {results.state.kind === "ready" && results.data && results.data.tiers.length === 0 && suggestions.length === 0 && smart.state.kind !== "loading" && <EmptyState icon={Search} message={onCreate ? "No conditions match. Create it below, or try different wording or a medication name." : "No conditions match. Try different wording, a medication name, or fewer characters."} />}
          {results.state.kind === "ready" &&
            results.data?.tiers.map((tier) => (
              <section key={tier.tier} aria-label={`${TIER_LABELS[tier.tier]} matches`} className="mb-3">
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{TIER_LABELS[tier.tier]}</h3>
                <ul className="divide-y divide-gray-100">
                  {tier.hits.map((hit) => (
                    <ConditionResultRow key={hit.conditionId} hit={hit} onPick={onPick} />
                  ))}
                </ul>
              </section>
            ))}
          {debounced.length >= SMART_MIN_LENGTH && (smart.state.kind === "loading" || suggestions.length > 0) && (
            <section aria-label="Suggested matches" className="mb-3">
              <h3 className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <Sparkles size={12} aria-hidden="true" />
                Suggested
                {smart.state.kind === "loading" && <span className="font-normal normal-case tracking-normal text-gray-400">searching the way Skynet does...</span>}
              </h3>
              {suggestions.length > 0 && (
                <ul className="divide-y divide-gray-100">
                  {suggestions.map((hit) => (
                    <ConditionResultRow key={hit.conditionId} hit={hit} onPick={onPick} />
                  ))}
                </ul>
              )}
            </section>
          )}
          {offerCreate && (
            <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-dashed border-gray-300 px-3 py-2">
              <span className="text-sm text-gray-600">No condition is named "{debounced}".</span>
              <Button variant="secondary" size="sm" icon={FilePlus2} onClick={() => onCreate(debounced)}>
                Create "{debounced}"
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
